import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useModal } from '../contexts/ModalContext'
import SettingsShell from '../components/settings/SettingsShell'
import McpPanel from '../components/settings/McpPanel'
import './SettingsPage.css'

// Site-admin-only sections are appended below after the isAdmin check at
// render time, so this base list is always visible regardless of role.
const BASE_SECTIONS = [
  { id: 'account', label: 'Account', icon: '👤' },
  { id: 'password', label: 'Change Password', icon: '🔑' },
]

// Gated on being able to DM at least one campaign — NOT on `isAdmin`. The site
// role is the wrong axis here in both directions: a campaign DM whose site role
// is 'player' is the main user of MCP and must see this, while a plain player
// gets nothing from MCP (shared/mcp/dispatch.js refuses every campaign they
// merely play in). `canUseMcp` below asks the same question the server does.
const MCP_SECTIONS = [{ id: 'mcp', label: 'Agent Access', icon: '🔗' }]

const ADMIN_SECTIONS = [
  { id: 'create-user', label: 'Create User', icon: '➕' },
  { id: 'user-passwords', label: 'User Passwords', icon: '🛡️' },
  { id: 'locked-accounts', label: 'Locked Accounts', icon: '🔒' },
  { id: 'create-campaign', label: 'Create Campaign', icon: '🧭' },
]

function SettingsPage() {
  // Site-role gate — deliberately `useAuth().isAdmin`, NOT
  // `useCampaign().canEdit`. This page manages SITE accounts (resetting any
  // user's password, unlocking accounts, minting new accounts), which must
  // stay restricted to the site admin. A campaign DM's `canEdit` is true for
  // their own campaign only and must never unlock these controls.
  const { user, logout, isAdmin, authEpoch } = useAuth()
  const navigate = useNavigate()
  const { alert: showAlert } = useModal()

  // ---- Section nav ----
  const [activeSection, setActiveSection] = useState('account')

  // Password change state
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [isChangingPassword, setIsChangingPassword] = useState(false)

  // Admin user management state
  const [users, setUsers] = useState([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState('')
  const [adminNewPassword, setAdminNewPassword] = useState('')
  const [adminConfirmPassword, setAdminConfirmPassword] = useState('')
  const [showAdminNewPassword, setShowAdminNewPassword] = useState(false)
  const [showAdminConfirmPassword, setShowAdminConfirmPassword] = useState(false)
  const [adminPasswordError, setAdminPasswordError] = useState('')
  const [isAdminChangingPassword, setIsAdminChangingPassword] = useState(false)
  const [unlockingUserId, setUnlockingUserId] = useState(null)

  // Admin: create user state
  const [newUserUsername, setNewUserUsername] = useState('')
  const [newUserPassword, setNewUserPassword] = useState('')
  const [newUserConfirmPassword, setNewUserConfirmPassword] = useState('')
  const [newUserRole, setNewUserRole] = useState('player')
  const [showNewUserPassword, setShowNewUserPassword] = useState(false)
  const [showNewUserConfirmPassword, setShowNewUserConfirmPassword] = useState(false)
  const [newUserError, setNewUserError] = useState('')
  const [isCreatingUser, setIsCreatingUser] = useState(false)

  // Admin: create campaign state
  const [newCampaignName, setNewCampaignName] = useState('')
  const [newCampaignSlug, setNewCampaignSlug] = useState('')
  const [campaignError, setCampaignError] = useState('')
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false)

  const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

  // Fetch users for admin. authEpoch: see the identical comment in
  // components/tabs/ItemsTab.jsx.
  useEffect(() => {
    if (isAdmin) {
      fetchUsers()
    }
  }, [isAdmin, authEpoch])

  // Whether to offer the Agent Access (MCP) section at all. MCP only reaches
  // campaigns the account DMs, so this asks the same question the server does
  // — NOT `isAdmin`, which is the SITE role and would hide the section from a
  // campaign DM whose site role is 'player'. `canEdit` per campaign is
  // site-admin OR that campaign's DM.
  const [canUseMcp, setCanUseMcp] = useState(false)

  useEffect(() => {
    let cancelled = false
    const check = async () => {
      if (isAdmin) {
        if (!cancelled) setCanUseMcp(true)
        return
      }
      try {
        const res = await fetch('/api/campaigns')
        const all = res.ok ? await res.json() : []
        if (!cancelled) setCanUseMcp(all.some((c) => c.canEdit))
      } catch {
        if (!cancelled) setCanUseMcp(false)
      }
    }
    check()
    return () => {
      cancelled = true
    }
  }, [isAdmin, authEpoch])

  const fetchUsers = async () => {
    setLoadingUsers(true)
    try {
      const response = await fetch('/api/users')
      if (response.ok) {
        const data = await response.json()
        setUsers(data)
      }
    } catch (err) {
      console.error('Failed to fetch users:', err)
    }
    setLoadingUsers(false)
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // Validate password requirements
  const validatePassword = (password, confirmPwd) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long'
    }
    if (password !== confirmPwd) {
      return 'Passwords do not match'
    }
    return ''
  }

  // Handle user's own password change
  const handleChangePassword = async (e) => {
    e.preventDefault()

    const error = validatePassword(newPassword, confirmPassword)
    if (error) {
      setPasswordError(error)
      return
    }

    setPasswordError('')
    setIsChangingPassword(true)

    try {
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          new_password: newPassword
        })
      })

      if (response.ok) {
        showAlert({
          title: 'Success',
          message: 'Your password has been changed successfully.',
          type: 'success'
        })
        setNewPassword('')
        setConfirmPassword('')
      } else {
        const data = await response.json()
        setPasswordError(data.error || 'Failed to change password')
      }
    } catch (err) {
      setPasswordError('Failed to change password. Please try again.')
    } finally {
      setIsChangingPassword(false)
    }
  }

  // Handle admin changing another user's password
  const handleAdminChangePassword = async (e) => {
    e.preventDefault()

    if (!selectedUserId) {
      setAdminPasswordError('Please select a user')
      return
    }

    const error = validatePassword(adminNewPassword, adminConfirmPassword)
    if (error) {
      setAdminPasswordError(error)
      return
    }

    setAdminPasswordError('')
    setIsAdminChangingPassword(true)

    try {
      const response = await fetch('/api/users', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: parseInt(selectedUserId),
          new_password: adminNewPassword
        })
      })

      if (response.ok) {
        const selectedUser = users.find(u => u.id === parseInt(selectedUserId))
        showAlert({
          title: 'Success',
          message: `Password for ${selectedUser?.username || 'user'} has been changed successfully.`,
          type: 'success'
        })
        setAdminNewPassword('')
        setAdminConfirmPassword('')
        setSelectedUserId('')
      } else {
        const data = await response.json()
        setAdminPasswordError(data.error || 'Failed to change password')
      }
    } catch (err) {
      setAdminPasswordError('Failed to change password. Please try again.')
    } finally {
      setIsAdminChangingPassword(false)
    }
  }

  // Handle unlocking a locked account
  const handleUnlockAccount = async (userId) => {
    setUnlockingUserId(userId)
    try {
      const response = await fetch('/api/users', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          action: 'unlock'
        })
      })

      if (response.ok) {
        const unlockedUser = users.find(u => u.id === userId)
        showAlert({
          title: 'Account Unlocked',
          message: `${unlockedUser?.username || 'User'}'s account has been unlocked successfully.`,
          type: 'success'
        })
        // Refresh users list to update lock status
        fetchUsers()
      } else {
        const data = await response.json()
        showAlert({
          title: 'Error',
          message: data.error || 'Failed to unlock account',
          type: 'error'
        })
      }
    } catch (err) {
      showAlert({
        title: 'Error',
        message: 'Failed to unlock account. Please try again.',
        type: 'error'
      })
    } finally {
      setUnlockingUserId(null)
    }
  }

  // Handle site-admin creating a new user account
  const handleCreateUser = async (e) => {
    e.preventDefault()

    const trimmedUsername = newUserUsername.trim()
    if (!trimmedUsername) {
      setNewUserError('Username is required')
      return
    }

    const error = validatePassword(newUserPassword, newUserConfirmPassword)
    if (error) {
      setNewUserError(error)
      return
    }

    setNewUserError('')
    setIsCreatingUser(true)

    try {
      const response = await fetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: trimmedUsername,
          password: newUserPassword,
          role: newUserRole,
        }),
      })

      if (response.ok) {
        const created = await response.json()
        setNewUserUsername('')
        setNewUserPassword('')
        setNewUserConfirmPassword('')
        setNewUserRole('player')
        // Refresh the user list so the new account is immediately
        // selectable in "Manage User Passwords" without a page reload.
        await fetchUsers()
        // Clear the loading flag before awaiting the confirmation modal —
        // otherwise the button reads "Creating…" for as long as the modal
        // stays open, even though the request itself is long done.
        setIsCreatingUser(false)
        await showAlert({
          title: 'User Created',
          message: `Account "${created.username}" was created successfully.`,
          type: 'success',
        })
        return
      } else {
        // Surface the server's message verbatim — e.g. the 409 for a
        // duplicate username — rather than guessing at a friendlier one.
        const data = await response.json().catch(() => ({}))
        setNewUserError(data.error || 'Failed to create user')
      }
    } catch (err) {
      setNewUserError('Failed to create user. Please try again.')
    } finally {
      setIsCreatingUser(false)
    }
  }

  // Handle site-admin creating a new campaign
  const handleCreateCampaign = async (e) => {
    e.preventDefault()

    if (!newCampaignName.trim()) {
      setCampaignError('Campaign name is required')
      return
    }
    if (!SLUG_PATTERN.test(newCampaignSlug)) {
      setCampaignError('Slug must be lowercase letters, numbers, and single hyphens (e.g. "sword-coast")')
      return
    }

    setCampaignError('')
    setIsCreatingCampaign(true)

    try {
      const response = await fetch('/api/campaigns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCampaignName.trim(), slug: newCampaignSlug }),
      })
      const data = await response.json()

      if (!response.ok) {
        setCampaignError(data.error || 'Failed to create campaign')
        return
      }

      navigate(`/c/${data.slug}/settings`)
    } catch (err) {
      setCampaignError('Failed to create campaign. Please try again.')
    } finally {
      // Every handler clears its loading flag in a finally, including this
      // one — even though the success path immediately navigates away and
      // unmounts this component, relying on unmount instead of an explicit
      // clear was the one inconsistency the brief called out.
      setIsCreatingCampaign(false)
    }
  }

  // Get locked users
  const lockedUsers = users.filter(u => u.is_locked)

  const sections = [
    ...BASE_SECTIONS,
    ...(canUseMcp ? MCP_SECTIONS : []),
    ...(isAdmin ? ADMIN_SECTIONS : []),
  ]

  return (
    <div className="sp-page-outer">
      <div className="sp-back-row">
        <a
          className="ss-btn ss-btn-neutral"
          href="/campaigns"
          onClick={(e) => { e.preventDefault(); navigate('/campaigns') }}
        >
          ← All campaigns
        </a>
      </div>

      <SettingsShell
        title="Settings"
        subtitle="Manage your account settings"
        icon="🧭"
        sections={sections}
        activeId={activeSection}
        onSelect={setActiveSection}
      >
        {activeSection === 'account' && (
          <section>
            <h2>Account Information</h2>
            <div className="sp-info-row">
              <span className="sp-info-label">Username:</span>
              <span className="sp-info-value">{user?.username}</span>
            </div>
            <div className="sp-info-row">
              <span className="sp-info-label">Site role:</span>
              <span className="sp-info-value">{user?.role === 'admin' ? 'Admin' : 'Player'}</span>
            </div>
            <div className="sp-account-actions">
              <button type="button" className="ss-btn ss-btn-danger" onClick={handleLogout}>
                🚪 Logout
              </button>
            </div>
          </section>
        )}

        {activeSection === 'password' && (
          <section>
            <h2>Change Password</h2>
            <form onSubmit={handleChangePassword} className="ss-form">
              <div className="ss-field">
                <label htmlFor="new-password">New Password</label>
                <div className="sp-password-wrapper">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    id="new-password"
                    className="ss-input"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    minLength={8}
                    required
                  />
                  <button
                    type="button"
                    className="sp-password-toggle"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    title={showNewPassword ? 'Hide password' : 'Show password'}
                  >
                    {showNewPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              <div className="ss-field">
                <label htmlFor="confirm-password">Confirm New Password</label>
                <div className="sp-password-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirm-password"
                    className="ss-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    minLength={8}
                    required
                  />
                  <button
                    type="button"
                    className="sp-password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    title={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              <p className="ss-panel-hint">Password must be at least 8 characters long</p>

              {passwordError && <p className="sp-error">{passwordError}</p>}

              <button type="submit" className="ss-btn ss-btn-primary" disabled={isChangingPassword}>
                {isChangingPassword ? 'Changing…' : 'Change Password'}
              </button>
            </form>
          </section>
        )}

        {canUseMcp && activeSection === 'mcp' && <McpPanel />}

        {isAdmin && activeSection === 'create-user' && (
          <section>
            <h2>Create User</h2>
            <p className="ss-panel-hint">
              Create a new site account. This is the only way to onboard a new DM or player
              without hand-editing the database.
            </p>
            <form onSubmit={handleCreateUser} className="ss-form">
              <div className="ss-field">
                <label htmlFor="new-user-username">Username</label>
                <input
                  type="text"
                  id="new-user-username"
                  className="ss-input"
                  value={newUserUsername}
                  onChange={(e) => setNewUserUsername(e.target.value)}
                  placeholder="Account username"
                  required
                />
              </div>

              <div className="ss-field-row">
                <div className="ss-field">
                  <label htmlFor="new-user-password">Password</label>
                  <div className="sp-password-wrapper">
                    <input
                      type={showNewUserPassword ? 'text' : 'password'}
                      id="new-user-password"
                      className="ss-input"
                      value={newUserPassword}
                      onChange={(e) => setNewUserPassword(e.target.value)}
                      placeholder="Enter password"
                      minLength={8}
                      required
                    />
                    <button
                      type="button"
                      className="sp-password-toggle"
                      onClick={() => setShowNewUserPassword(!showNewUserPassword)}
                      title={showNewUserPassword ? 'Hide password' : 'Show password'}
                    >
                      {showNewUserPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>

                <div className="ss-field">
                  <label htmlFor="new-user-confirm-password">Confirm Password</label>
                  <div className="sp-password-wrapper">
                    <input
                      type={showNewUserConfirmPassword ? 'text' : 'password'}
                      id="new-user-confirm-password"
                      className="ss-input"
                      value={newUserConfirmPassword}
                      onChange={(e) => setNewUserConfirmPassword(e.target.value)}
                      placeholder="Confirm password"
                      minLength={8}
                      required
                    />
                    <button
                      type="button"
                      className="sp-password-toggle"
                      onClick={() => setShowNewUserConfirmPassword(!showNewUserConfirmPassword)}
                      title={showNewUserConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showNewUserConfirmPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="ss-field sp-field-role">
                <label htmlFor="new-user-role">Site Role</label>
                <select
                  id="new-user-role"
                  className="ss-select"
                  value={newUserRole}
                  onChange={(e) => setNewUserRole(e.target.value)}
                >
                  <option value="player">Player</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <p className="ss-panel-hint">Password must be at least 8 characters long</p>

              {newUserError && <p className="sp-error">{newUserError}</p>}

              <button type="submit" className="ss-btn ss-btn-primary" disabled={isCreatingUser}>
                {isCreatingUser ? 'Creating…' : 'Create User'}
              </button>
            </form>
          </section>
        )}

        {isAdmin && activeSection === 'user-passwords' && (
          <section>
            <h2>Manage User Passwords</h2>
            <p className="ss-panel-hint">Set a new password for any user account.</p>

            {loadingUsers ? (
              <p className="sp-loading-text">Loading users…</p>
            ) : (
              <form onSubmit={handleAdminChangePassword} className="ss-form">
                <div className="ss-field">
                  <label htmlFor="select-user">Select User</label>
                  <select
                    id="select-user"
                    className="ss-select"
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    required
                  >
                    <option value="">-- Select a user --</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.username} ({u.role})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ss-field">
                  <label htmlFor="admin-new-password">New Password</label>
                  <div className="sp-password-wrapper">
                    <input
                      type={showAdminNewPassword ? 'text' : 'password'}
                      id="admin-new-password"
                      className="ss-input"
                      value={adminNewPassword}
                      onChange={(e) => setAdminNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      minLength={8}
                      required
                    />
                    <button
                      type="button"
                      className="sp-password-toggle"
                      onClick={() => setShowAdminNewPassword(!showAdminNewPassword)}
                      title={showAdminNewPassword ? 'Hide password' : 'Show password'}
                    >
                      {showAdminNewPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>

                <div className="ss-field">
                  <label htmlFor="admin-confirm-password">Confirm New Password</label>
                  <div className="sp-password-wrapper">
                    <input
                      type={showAdminConfirmPassword ? 'text' : 'password'}
                      id="admin-confirm-password"
                      className="ss-input"
                      value={adminConfirmPassword}
                      onChange={(e) => setAdminConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      minLength={8}
                      required
                    />
                    <button
                      type="button"
                      className="sp-password-toggle"
                      onClick={() => setShowAdminConfirmPassword(!showAdminConfirmPassword)}
                      title={showAdminConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showAdminConfirmPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>

                <p className="ss-panel-hint">Password must be at least 8 characters long</p>

                {adminPasswordError && <p className="sp-error">{adminPasswordError}</p>}

                <button type="submit" className="ss-btn ss-btn-primary" disabled={isAdminChangingPassword}>
                  {isAdminChangingPassword ? 'Changing…' : 'Set User Password'}
                </button>
              </form>
            )}
          </section>
        )}

        {isAdmin && activeSection === 'locked-accounts' && (
          <section>
            <h2>Locked Accounts</h2>
            <p className="ss-panel-hint">Accounts locked due to failed login attempts.</p>

            {loadingUsers ? (
              <p className="sp-loading-text">Loading…</p>
            ) : lockedUsers.length === 0 ? (
              <p className="sp-empty-text">No accounts are currently locked.</p>
            ) : (
              <div className="sp-locked-list">
                {lockedUsers.map(u => (
                  <div key={u.id} className="sp-locked-item">
                    <div className="sp-locked-info">
                      <span className="sp-locked-username">{u.username}</span>
                      <span className="sp-locked-role">({u.role})</span>
                      <span className="sp-locked-time">
                        Locked for {u.lock_minutes_remaining} minute{u.lock_minutes_remaining !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="ss-btn ss-btn-primary"
                      onClick={() => handleUnlockAccount(u.id)}
                      disabled={unlockingUserId === u.id}
                    >
                      {unlockingUserId === u.id ? 'Unlocking…' : '🔓 Unlock'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {isAdmin && activeSection === 'create-campaign' && (
          <section>
            <h2>Create a Campaign</h2>
            <p className="ss-panel-hint">
              Start a new campaign. It's created empty — assign its DM from that
              campaign's settings page.
            </p>

            <form onSubmit={handleCreateCampaign} className="ss-form">
              <div className="ss-field">
                <label htmlFor="new-campaign-name">Campaign Name</label>
                <input
                  type="text"
                  id="new-campaign-name"
                  className="ss-input"
                  value={newCampaignName}
                  onChange={(e) => setNewCampaignName(e.target.value)}
                  placeholder="Campaign name"
                  required
                />
              </div>

              <div className="ss-field">
                <label htmlFor="new-campaign-slug">URL Slug</label>
                <input
                  type="text"
                  id="new-campaign-slug"
                  className="ss-input"
                  value={newCampaignSlug}
                  onChange={(e) => setNewCampaignSlug(e.target.value)}
                  placeholder="url-slug"
                  required
                />
                <p className="ss-panel-hint">Lowercase letters, numbers, and single hyphens (e.g. "sword-coast")</p>
              </div>

              {campaignError && <p className="sp-error">{campaignError}</p>}

              <button type="submit" className="ss-btn ss-btn-primary" disabled={isCreatingCampaign}>
                {isCreatingCampaign ? 'Creating…' : 'Create Campaign'}
              </button>
            </form>
          </section>
        )}
      </SettingsShell>
    </div>
  )
}

export default SettingsPage
